import { useState } from 'react';
import {
  Box, Grid, Stack, Typography, Card, CardActionArea, Radio, Checkbox,
  Divider, Button, IconButton, TextField, Chip, Paper,
  InputAdornment, MenuItem, List, ListItem, ListItemIcon, ListItemText, Skeleton,
  Dialog, DialogContent, Alert, CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateWarehouse, toggleProzoWarehouse, addNonProzoWarehouse,
  removeNonProzoWarehouse,
} from '../../../../redux/pricingWizardSlice';
import { useGetWmsWarehouseQuery } from '../../../../redux/commonApiCalls';
import { useRegisterPickupMutation } from '../../../../redux/wizardApiSlice';
import SectionTitle from './SectionTitle';
import { INDIAN_STATES, gstStateCode, isValidGstinFormat, gstinMatchesState } from '../constants';

const REGION_COLORS = {
  NORTH:     { bg: '#dbeafe', fg: '#1e40af' },
  SOUTH:     { bg: '#dcfce7', fg: '#166534' },
  EAST:      { bg: '#fef9c3', fg: '#854d0e' },
  WEST:      { bg: '#fce7f3', fg: '#9d174d' },
  CENTRAL:   { bg: '#ede9fe', fg: '#5b21b6' },
  NORTHEAST: { bg: '#ffedd5', fg: '#9a3412' },
  PAN_INDIA: { bg: '#f1f5f9', fg: '#334155' },
};

function regionChipStyle(region) {
  const key = region?.toUpperCase().replace(/[\s-]/g, '_');
  return REGION_COLORS[key] ?? { bg: '#f1f5f9', fg: '#475569' };
}

const PICKUP_MODES = [
  { id: 'yes', label: 'Yes · all Prozo warehouses', sub: 'Pick up only from Prozo-managed warehouses.' },
  { id: 'no', label: 'No · merchant warehouses only', sub: 'Merchant ships from their own locations.' },
  { id: 'mixed', label: 'Mixed', sub: 'Some Prozo + some merchant locations.' },
];

function ChoiceCard({ checked, onClick, title, sub }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: checked ? 'primary.lighter' : 'background.paper',
        '&:hover': { borderColor: 'primary.light', bgcolor: checked ? 'primary.lighter' : (t) => alpha(t.palette.primary.main, 0.04) },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Radio checked={checked} sx={{ p: 0.25, pointerEvents: 'none' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>{sub}</Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export default function WarehouseCard() {
  const dispatch = useDispatch();
  const warehouse = useSelector((s) => s.pricingWizard.warehouse);
  const merchantId = useSelector((s) => s.pricingWizard.merchantId);
  const signupEmail = useSelector((s) => s.pricingWizard.signup.email);
  const [registerPickup, { isLoading: savingPickup }] = useRegisterPickupMutation();
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const EMPTY_PICKUP = { name: '', contact_name: '', contact_number: '', email: '', gstin: '', line1: '', line2: '', city: '', state: '', pincode: '' };
  const [addingPickup, setAddingPickup] = useState(false);
  const [newPickup, setNewPickup] = useState(EMPTY_PICKUP);
  const [pickupApiError, setPickupApiError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // id of address pending deletion
  const [deletingId, setDeletingId] = useState(null);           // id currently being deactivated via API

  const { data: wmsWarehouses = [], isLoading: warehousesLoading } = useGetWmsWarehouseQuery();

  const filteredWarehouses = wmsWarehouses.filter((w) => {
    if (!warehouseSearch) return true;
    const q = warehouseSearch.toLowerCase();
    const name = w.globalName ?? w.displayName ?? w.name ?? w.warehouseName ?? '';
    const location = [w.city, w.state].filter(Boolean).join(' ');
    return name.toLowerCase().includes(q) || location.toLowerCase().includes(q);
  });

  const showProzo = warehouse.is_prozo === 'yes' || warehouse.is_prozo === 'mixed';
  const showNonProzo = warehouse.is_prozo === 'no' || warehouse.is_prozo === 'mixed';

  const gstinError = newPickup.gstin
    ? (!isValidGstinFormat(newPickup.gstin)
        ? 'Enter a valid 15-character GSTIN.'
        : (newPickup.state && !gstinMatchesState(newPickup.gstin, newPickup.state)
            ? `GSTIN must begin with ${gstStateCode(newPickup.state)} for ${newPickup.state}.`
            : ''))
    : '';

  return (
    <Box>
      <Divider sx={{ my: 3 }} />
      <SectionTitle icon="📦" title="Pickup / Warehouse" />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, mb: 1.5 }}>
        Specify where this merchant's shipments will be picked from. Prozo warehouses are managed by Prozo;
        merchant warehouses need address details for our pickup routing.
      </Typography>

      <Grid container spacing={1.5}>
        {PICKUP_MODES.map((m) => (
          <Grid item xs={12} md={4} key={m.id}>
            <ChoiceCard
              checked={warehouse.is_prozo === m.id}
              onClick={() => {
                if (warehouse.is_prozo === m.id) return;
                // Only change the mode — never clear pickup addresses automatically
                dispatch(updateWarehouse({ is_prozo: m.id, prozo_warehouses: [] }));
              }}
              title={m.label}
              sub={m.sub}
            />
          </Grid>
        ))}
      </Grid>

      {showProzo && (
        <Box sx={{ mt: 2 }}>
          {/* header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                Prozo Warehouses
              </Typography>
              {!warehousesLoading && (
                <Chip size="small" color="primary" variant="outlined"
                  label={`${warehouse.prozo_warehouses.length} / ${wmsWarehouses.length} selected`}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
              )}
            </Stack>
            {warehouse.prozo_warehouses.length > 0 && (
              <Button size="small" color="error" variant="text"
                onClick={() => dispatch(updateWarehouse({ prozo_warehouses: [] }))}
                sx={{ fontSize: 11.5, fontWeight: 600 }}>
                Clear all
              </Button>
            )}
          </Stack>

          {warehousesLoading ? (
            <Stack spacing={0.75}>
              {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
            </Stack>
          ) : wmsWarehouses.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">No eligible Prozo warehouses found.</Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {/* search */}
              <Box sx={{ p: 1.25, borderBottom: 1, borderColor: 'divider' }}>
                <TextField
                  size="small" fullWidth
                  placeholder={`Search ${wmsWarehouses.length} warehouses…`}
                  value={warehouseSearch}
                  onChange={(e) => setWarehouseSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
                    endAdornment: warehouseSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setWarehouseSearch('')}>
                          <ClearIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                {warehouseSearch && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.75 }}>
                    {filteredWarehouses.length} of {wmsWarehouses.length} matching
                  </Typography>
                )}
              </Box>

              {/* scrollable list */}
              <List dense disablePadding
                sx={{ maxHeight: 320, overflowY: 'auto', '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 99 } }}>
                {filteredWarehouses.length === 0 ? (
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText primary={<Typography variant="body2" color="text.secondary" align="center">No warehouses match your search.</Typography>} />
                  </ListItem>
                ) : filteredWarehouses.map((w, idx) => {
                  const displayName = w.globalName ?? w.displayName ?? w.name ?? w.warehouseName ?? w.label ?? w.id ?? w._id;
                  const location = [w.city, w.state].filter(Boolean).join(', ');
                  const checked = warehouse.prozo_warehouses.includes(displayName);
                  return (
                    <ListItem
                      key={displayName}
                      disablePadding
                      divider={idx < filteredWarehouses.length - 1}
                      onClick={() => dispatch(toggleProzoWarehouse(displayName))}
                      sx={{
                        px: 1.5, py: 0.75, cursor: 'pointer',
                        bgcolor: checked ? (t) => alpha(t.palette.primary.main, 0.05) : 'transparent',
                        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, checked ? 0.08 : 0.04) },
                        transition: 'background-color .15s',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox size="small" checked={checked} sx={{ p: 0, pointerEvents: 'none' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: 13, fontWeight: checked ? 700 : 500, color: checked ? 'primary.dark' : 'text.primary', lineHeight: 1.3 }}>
                            {displayName}
                          </Typography>
                        }
                        secondary={
                          <Stack direction="row" flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                            {w.name && w.name !== displayName && (
                              <Chip label={w.name} size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 600, pointerEvents: 'none', bgcolor: '#dbeafe', color: '#1e3a8a' }} />
                            )}
                            {location && (
                              <Chip label={location} size="small"
                                sx={{ height: 18, fontSize: 10, pointerEvents: 'none', bgcolor: '#f1f5f9', color: '#475569' }} />
                            )}
                            {w.region && (() => {
                              const rc = regionChipStyle(w.region);
                              return (
                                <Chip label={w.region} size="small"
                                  sx={{ height: 18, fontSize: 10, fontWeight: 700, pointerEvents: 'none', bgcolor: rc.bg, color: rc.fg }} />
                              );
                            })()}
                            {w.utilizationCategory && (
                              <Chip label={w.utilizationCategory} size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, pointerEvents: 'none', bgcolor: '#ede9fe', color: '#5b21b6' }} />
                            )}
                          </Stack>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {showNonProzo && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                Pickup Addresses
              </Typography>
              <Chip size="small" color="primary" variant="outlined"
                label={`${warehouse.non_prozo.length} added`}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
            </Stack>
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              onClick={() => setAddingPickup(true)} disableElevation sx={{ borderRadius: '8px' }}>
              Add address
            </Button>
          </Stack>

          {/* Existing addresses */}
          {warehouse.non_prozo.length === 0 && !addingPickup && (
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">
                No pickup addresses added yet. Click <b>Add address</b> to add one.
              </Typography>
            </Paper>
          )}

          <Stack spacing={1.25}>
            {warehouse.non_prozo.map((wh, idx) => (
              <Paper key={wh.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                      <Chip label={idx === 0 ? 'Primary' : `#${idx + 1}`} size="small"
                        color={idx === 0 ? 'primary' : 'default'} sx={{ height: 18, fontSize: 9.5, fontWeight: 700 }} />
                      {wh.name && (
                        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{wh.name}</Typography>
                      )}
                      {wh.contact_name && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{wh.contact_name}</Typography>
                      )}
                      {wh.contact_number && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{wh.contact_number}</Typography>
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>
                      {[wh.line1, wh.line2, wh.city, wh.state, wh.pincode].filter(Boolean).join(', ')}
                    </Typography>
                    {wh.gstin && (
                      <Chip label={wh.gstin} size="small" sx={{ mt: 0.5, height: 18, fontSize: 10, fontFamily: 'monospace', fontWeight: 600 }} />
                    )}
                  </Box>
                  <IconButton size="small" color="error" disabled={deletingId === wh.id}
                    onClick={() => setConfirmDeleteId(wh.id)}>
                    {deletingId === wh.id
                      ? <CircularProgress size={16} color="error" />
                      : <DeleteOutlineIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </Paper>
            ))}
          </Stack>

          {/* Add address modal */}
          <Dialog
            open={addingPickup}
            onClose={() => { if (!savingPickup) { setAddingPickup(false); setNewPickup(EMPTY_PICKUP); setPickupApiError(null); } }}
            maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            {/* Header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>Add Pickup Address</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>New warehouse or pickup location for this merchant</Typography>
                </Box>
              </Stack>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5 }}>
              {pickupApiError && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setPickupApiError(null)}>
                  {pickupApiError}
                </Alert>
              )}

              {/* 1. State + City */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>
                Location <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth select label="State"
                    value={newPickup.state}
                    onChange={(e) => setNewPickup((p) => ({ ...p, state: e.target.value }))}>
                    <MenuItem value="">— select —</MenuItem>
                    {INDIAN_STATES.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="City" placeholder="New Delhi"
                    value={newPickup.city}
                    onChange={(e) => setNewPickup((p) => ({ ...p, city: e.target.value }))} />
                </Grid>
              </Grid>

              {/* 2. Warehouse name */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>
                Warehouse Name
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Warehouse Name"
                    placeholder="e.g. BrandName_Location_Warehouse1"
                    helperText="Suggested format: BrandName_Location_Warehouse number"
                    value={newPickup.name}
                    onChange={(e) => setNewPickup((p) => ({ ...p, name: e.target.value }))} />
                </Grid>
              </Grid>

              {/* 3. Address */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>
                Address <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Address Line 1" placeholder="Door no., street, locality"
                    value={newPickup.line1}
                    onChange={(e) => setNewPickup((p) => ({ ...p, line1: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Address Line 2 (optional)" placeholder="Area, landmark"
                    value={newPickup.line2}
                    onChange={(e) => setNewPickup((p) => ({ ...p, line2: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="Pincode" placeholder="6-digit"
                    inputProps={{ maxLength: 6 }}
                    value={newPickup.pincode}
                    onChange={(e) => setNewPickup((p) => ({ ...p, pincode: e.target.value }))} />
                </Grid>
              </Grid>

              {/* 4. Contact */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>
                Contact Details <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="Contact Name" placeholder="Pickup contact person"
                    value={newPickup.contact_name}
                    onChange={(e) => setNewPickup((p) => ({ ...p, contact_name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="Contact Number" placeholder="10-digit mobile"
                    inputProps={{ maxLength: 10 }}
                    value={newPickup.contact_number}
                    onChange={(e) => setNewPickup((p) => ({ ...p, contact_number: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="Email" placeholder="pickup@company.com" type="email"
                    value={newPickup.email || ''}
                    onChange={(e) => setNewPickup((p) => ({ ...p, email: e.target.value }))} />
                </Grid>
              </Grid>

              {/* 5. GSTIN — state-aware validation */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>
                Tax <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="GSTIN" placeholder="22AAAAA0000A1Z5"
                    inputProps={{ maxLength: 15 }}
                    value={newPickup.gstin}
                    error={Boolean(gstinError)}
                    helperText={gstinError || (newPickup.state ? `Enter the GSTIN registered for ${newPickup.state} — it must begin with ${gstStateCode(newPickup.state)}.` : 'Select a state to validate the GSTIN prefix.')}
                    onChange={(e) => setNewPickup((p) => ({ ...p, gstin: e.target.value.toUpperCase() }))} />
                </Grid>
              </Grid>
            </DialogContent>

            <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button color="inherit" disabled={savingPickup}
                onClick={() => { setAddingPickup(false); setNewPickup(EMPTY_PICKUP); setPickupApiError(null); }}>
                Cancel
              </Button>
              <Button variant="contained" disableElevation
                disabled={!newPickup.line1 || !newPickup.city || !newPickup.state || !newPickup.contact_name || !newPickup.contact_number || !newPickup.email || !newPickup.gstin || Boolean(gstinError) || savingPickup}
                startIcon={savingPickup ? <CircularProgress size={15} color="inherit" /> : null}
                onClick={async () => {
                  setPickupApiError(null);
                  const isFirst = warehouse.non_prozo.length === 0;
                  if (merchantId) {
                    try {
                      await registerPickup({
                        merchantId,
                        status: 'ACTIVE',
                        address_type: 'PICKUP',
                        country: 'India',
                        shipmentType: 'B2B',
                        email: newPickup.email || signupEmail || '',
                        name: newPickup.name || newPickup.contact_name || newPickup.city,
                        address_line: [newPickup.line1, newPickup.line2].filter(Boolean).join(', '),
                        city: newPickup.city,
                        state: newPickup.state,
                        pincode: newPickup.pincode,
                        contactNumber: newPickup.contact_number || '',
                        gstin: newPickup.gstin || '',
                        is_default: isFirst,
                      }).unwrap();
                      // Only add to list on API success — mark _registered so the bulk loop skips it
                      dispatch(addNonProzoWarehouse({ ...newPickup, label: newPickup.name || newPickup.contact_name || newPickup.city, _registered: true }));
                      setNewPickup(EMPTY_PICKUP);
                      setAddingPickup(false);
                    } catch (err) {
                      setPickupApiError(err?.data?.message || err?.message || 'Failed to save address — please retry');
                    }
                  } else {
                    // No merchantId yet — save locally only
                    dispatch(addNonProzoWarehouse({ ...newPickup, label: newPickup.name || newPickup.contact_name || newPickup.city }));
                    setNewPickup(EMPTY_PICKUP);
                    setAddingPickup(false);
                  }
                }}>
                {savingPickup ? 'Saving…' : 'Save address'}
              </Button>
            </Box>
          </Dialog>

          {/* Confirm delete dialog */}
          <Dialog open={Boolean(confirmDeleteId)} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 2.5 } }}>
            <Box sx={{ p: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15.5, mb: 0.75 }}>Remove pickup address?</Typography>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2.5 }}>
                This will mark the address as inactive on the backend. You can add it again if needed.
              </Typography>
              <Stack direction="row" spacing={1.25} justifyContent="flex-end">
                <Button color="inherit" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                <Button variant="contained" color="error" disableElevation
                  onClick={async () => {
                    const id = confirmDeleteId;
                    setConfirmDeleteId(null);
                    const wh = warehouse.non_prozo.find((w) => w.id === id);
                    if (merchantId && wh) {
                      setDeletingId(id);
                      try {
                        await registerPickup({
                          merchantId,
                          id: wh.id,
                          name: wh.contact_name || wh.label || wh.city,
                          address_line: [wh.line1, wh.line2].filter(Boolean).join(', '),
                          city: wh.city,
                          state: wh.state,
                          pincode: wh.pincode,
                          contactNumber: wh.contact_number || '',
                          email: wh.email || signupEmail || '',
                          gstin: wh.gstin || '',
                          country: 'India',
                          shipmentType: 'B2B',
                          address_type: 'PICKUP',
                          status: 'INACTIVE',
                          is_deleted: '1',
                        }).unwrap();
                      } catch { /* ignore — remove locally regardless */ }
                      setDeletingId(null);
                    }
                    dispatch(removeNonProzoWarehouse(id));
                  }}>
                  Yes, remove
                </Button>
              </Stack>
            </Box>
          </Dialog>
        </Box>
      )}
    </Box>
  );
}
